import Boom = require("boom");
import { inject, injectable } from "inversify";
import { CloudProvider } from "./CloudProvider";
import { Instance } from "./Instance";
import { VHost } from "./VHost";
import { Alias } from "./Alias";
import TYPES from "../../TYPES";
import { ServiceConfiguration } from "../../utils/ServiceConfiguration";
import { AzureVmCloudProvider } from "../../providers/AzurevmWorker";
import { DigitalOceanCloudProvider } from "../../providers/DigitalOceanWorker";
import { AWSCloudProvider } from "../../providers/EC2Worker";


const __rp = require('request-promise').defaults({ json: true });     // tslint:disable-line
let rp;

class Cache {
    static jwt: string;
    static accessToken: string;
}

export interface KotennaConfig {
    /** Auth0 user to authenticate requests */
    USER: string;

    /** Auth0 user password to authenticate requests */
    PASSWORD: string;
}

export interface CliCredentials {
    name: string;
    password: string;
}

export interface KotennaApi {

    login(): Promise<string>;

    vhost(): Promise<VHost[]>;

    providers(): Promise<CloudProvider[]>;

    instances(provider: string): Promise<Instance[]>;

    instanceTpl(provider: string, instanceId: string): Promise<any>;

    vhostSave(vhost: VHost): Promise<VHost>;

    vhostDelete(hostname: string): Promise<any>;

    vhostStop(hostname: string): Promise<any>;

    providerSave(provider: CloudProvider|DigitalOceanCloudProvider|AzureVmCloudProvider|AWSCloudProvider): Promise<CloudProvider|DigitalOceanCloudProvider|AzureVmCloudProvider|AWSCloudProvider>;

    userEmail(): string;

    /**  This method BYPASS any existing authentication method, and relies only on the given BEARER accessToken */
    credentials(accessToken: string, name: string): Promise<CliCredentials>;

    recordCreate(record: string): Promise<any>;

    alias(): Promise<Alias[]>;

    aliasFromHostname(hostname: string): Promise<Alias[]>;

    aliasSave(alias: Alias): Promise<Alias>;

    aliasDelete(aliasname: string): Promise<any>;
}

@injectable()
export class KotennaApiv1 {

    private readonly stage = process.env.STAGE || "prod";

    private readonly USER_AGENT = `porcli/${require("../../../package").version}`;

    private readonly config: ServiceConfiguration;

    constructor(@inject(TYPES.ServiceConfiguration) props: ServiceConfiguration) {
        this.config = props;
    }

    /**
     * The API url
     */
    public url(): string {
        return `https://${this.stage}.kotenna.porketta.io`;
    }

    /**
     * DO NOT CALL THIS METHOD
     * Clients should never call directly this method.
     */
    private loginImpl(): Promise<any> {
        if (this.isAuthenticated()) {
            if (rp) {
                return Promise.resolve(rp);
            }
            else {
                rp = __rp.defaults({
                    headers: {
                        "Authorization": `Bearer ${this.getAccessToken()}`,
                        "User-Agent": this.USER_AGENT
                    }
                });
                return rp;
            }
        }
        const PASSWORD = this.config.getConfiguration().password;
        const USER = this.config.getConfiguration().username;
        return __rp({
            method: "POST",
            uri: `https://porketta.eu.auth0.com/oauth/token`,
            body: {
                grant_type: "password",
                username: USER,
                password: PASSWORD,
                audience: "https://kotenna.porketta.eu",
                client_id: "R1Uti0BfrWofPs68Lt2GUwyUxckJdbD5"
            }
        })
            .then(response => {
                const base64Url = response.access_token.split(".")[1];
                const base64 = base64Url.replace("-", "+").replace("_", "/");
                const token = JSON.parse(Buffer.from(base64, "base64").toString());
                this.setToken(token);
                this.setAccessToken(response.access_token);

                rp = __rp.defaults({
                    headers: {
                        "Authorization": `Bearer ${response.access_token}`,
                        "User-Agent": this.USER_AGENT
                    }
                });
                return rp;
            })
            .catch(err => {
                throw Boom.boomify(err, { statusCode: err.statusCode });
            });
    }

    /**
     * Resolve with the user email
     */
    public login(): Promise<string> {
        return this
            .loginImpl()
            .then(() => this.userEmail());
    }

    private apiCall(request: any): any {
        return this.loginImpl().then(rp => {
            return rp(request);
        })
            .catch(err => {
                throw Boom.boomify(err, { statusCode: err.statusCode });
            });
    }

    private getToken(): any {
        return Cache.jwt;
    }

    private setToken(jwt: any): void {
        Cache.jwt = jwt;
    }

    private setAccessToken(token: string): void {
        Cache.accessToken = token;
    }

    private getAccessToken(): string {
        return Cache.accessToken;
    }

    /**
     * @return {boolean} true if the user is logged in
     */
    public isAuthenticated(): boolean {
        const token = this.getToken();
        return token && token.exp * 1000 > Date.now();
    }

    /**
     * @return {string} the email that identifies the logged in user, or null if the user is not authenticated
     */
    public userEmail(): string {
        const token = this.getToken();
        if (token) {
            return token["https://kotenna.porketta.eu/group"];
        }
        else {
            throw Boom.forbidden("User is not logged in");
        }
    }

    public userID(): string {
        return this.userEmail();
    }

    /**
     * @return {string} the group for the current user
     */
    public userGroup(): string {
        const token = this.getToken();
        if (token) {
            return token["https://kotenna.porketta.eu/email"];
        }
        else {
            throw Boom.forbidden("User is not logged in");
        }
    }

    /**
     * Returns all the provider owned by the current user's group
     */
    public async providers(): Promise<CloudProvider[]> {
        return this.apiCall({ uri: `${this.url()}/provider` });
    }

    /**
     * Return the saved CloudProvider
     * @param provider
     */
    public providerSave(provider: CloudProvider): Promise<CloudProvider> {
        return this.apiCall({
            uri: `${this.url()}/provider`,
            method: "POST",
            body: { provider }
        });
    }

    public providerDelete(providerName: string): Promise<CloudProvider> {
        return this.apiCall({
            uri: `${this.url()}/provider/${providerName}`,
            method: "DELETE"
        });
    }

    /**
     * List all the availbale instances for a given CloudProvider
     * @param provider {string} The CloudProvider name 
     */
    public instances(provider: string): Promise<Instance[]> {
        return this.apiCall({ uri: `${this.url()}/instances/${provider}` });
    }

    /**
     * Generates a tpl for the instance identified b the given CloudProvider id and instance id
     * @param provider 
     * @param instanceId 
     */
    public instanceTpl(provider: string, instanceId: string): Promise<any> {
        return this.apiCall({ uri: `${this.url()}/instances/${provider}/${encodeURIComponent(instanceId)}` });
    }

    public vhost(): Promise<VHost[]> {
        return this.apiCall({ uri: `${this.url()}/vhost` });
    }

    public vhostSave(vhost: VHost): Promise<VHost> {
        return this.apiCall({
            uri: `${this.url()}/vhost/${vhost.hostname}`,
            method: "PUT",
            body: vhost
        });
    }

    public vhostDelete(hostname: string): Promise<any> {
        return this.apiCall({
            uri: `${this.url()}/vhost/${hostname}`,
            method: "DELETE"
        });
    }

    public vhostStop(hostname: string): Promise<any> {
        return this.apiCall({
            method: 'PUT',
            uri: `${this.url()}/vhost/${hostname}/state`,
            body: { status: "stopVhost" }
        });
    }

    public async credentials(accessToken: string, name: string): Promise<CliCredentials> {

        const res:CliCredentials = await __rp({
            uri: `${this.url()}/credentials`,
            method: 'PUT',
            body: { name },
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "User-Agent": this.USER_AGENT
            }
        });

        return res;
    };

    public async recordCreate(record: string): Promise<any> {
        return this.apiCall({
            uri: `${this.url()}/domain`,
            method: "POST",
            body: { record }
        });
    }

    alias(): Promise<Alias[]> {
        return this.apiCall({ uri: `${this.url()}/alias` });
    }

    aliasFromHostname(hostname): Promise<Alias[]> {
        return this.apiCall({ uri: `${this.url()}/alias/vhost/${hostname}` });
    }

    aliasSave(alias: Alias): Promise<Alias> {
        return this.apiCall({ 
            uri: `${this.url()}/alias`,
            method: "PUT",
            body: { alias }
        });
    }
    
    aliasDelete(aliasname: string): Promise<any> {
        return this.apiCall({ 
            uri: `${this.url()}/alias/${aliasname}`,
            method: "DELETE",
        });
    }
}